import { Headphones } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { supportChatApi, type SupportChatEvent } from "@/api";
import { buildApiUrl } from "@/config/env";
import { auth } from "@/lib/auth";
import { CUSTOMER_AUTH_CHANGED_EVENT } from "@/lib/app-notify";

const SUPPORT_CHAT_UNREAD_REFRESH_EVENT = "athome:support-chat-unread-refresh";

const isSupportChatEvent = (payload: SupportChatEvent) => {
  return typeof payload.type === "string" && payload.type.startsWith("support.");
};

const buildWsUrl = () => {
  const token = auth.getToken();
  if (!token || typeof window === "undefined") {
    return "";
  }
  const url = buildApiUrl("/support-chat/ws");
  const base = url.startsWith("http") ? url : `${window.location.origin}${url}`;
  return `${base.replace(/^http/i, "ws")}?token=${encodeURIComponent(token)}`;
};

export default function SupportChatUnreadBubble() {
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const socketRef = useRef<WebSocket | null>(null);
  const heartbeatRef = useRef<number | null>(null);
  const reconnectRef = useRef<number | null>(null);
  const closedByPageRef = useRef(false);

  const hidden = useMemo(() => {
    return unread <= 0 || location.pathname.startsWith("/support-chat") || !auth.getToken();
  }, [location.pathname, unread]);

  const refreshUnread = async () => {
    if (!auth.getToken()) {
      setUnread(0);
      return;
    }
    const result = await supportChatApi.getUnreadCount();
    setUnread(Number(result.unread_count ?? result.unreadCount ?? 0));
  };

  const clearTimers = () => {
    if (heartbeatRef.current) {
      window.clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (reconnectRef.current) {
      window.clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
  };

  const connectSocket = () => {
    const wsUrl = buildWsUrl();
    const current = socketRef.current;
    if (!wsUrl || current?.readyState === WebSocket.OPEN || current?.readyState === WebSocket.CONNECTING) {
      return;
    }
    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;
    socket.onopen = () => {
      if (heartbeatRef.current) {
        window.clearInterval(heartbeatRef.current);
      }
      heartbeatRef.current = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send("ping");
        }
      }, 25000);
    };
    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data) as SupportChatEvent;
        if (!isSupportChatEvent(payload)) {
          return;
        }
        if (payload.type !== "support.pong") {
          const nextUnread = payload.conversation?.userUnreadCount;
          if (typeof nextUnread === "number") {
            setUnread(nextUnread);
          }
        }
      } catch {
        return;
      }
    };
    socket.onclose = () => {
      if (heartbeatRef.current) {
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (!closedByPageRef.current && !reconnectRef.current) {
        reconnectRef.current = window.setTimeout(() => {
          reconnectRef.current = null;
          connectSocket();
        }, 3000);
      }
    };
    socket.onerror = () => {
      socket.close();
    };
  };

  useEffect(() => {
    closedByPageRef.current = false;
    refreshUnread().catch(() => undefined);
    connectSocket();
    const poll = window.setInterval(() => {
      refreshUnread().catch(() => undefined);
    }, 10000);
    const handleAuthChanged = () => {
      socketRef.current?.close();
      socketRef.current = null;
      refreshUnread().catch(() => undefined);
      connectSocket();
    };
    const handleUnreadRefresh = () => {
      refreshUnread().catch(() => undefined);
    };
    window.addEventListener(CUSTOMER_AUTH_CHANGED_EVENT, handleAuthChanged);
    window.addEventListener(SUPPORT_CHAT_UNREAD_REFRESH_EVENT, handleUnreadRefresh);
    return () => {
      closedByPageRef.current = true;
      window.clearInterval(poll);
      window.removeEventListener(CUSTOMER_AUTH_CHANGED_EVENT, handleAuthChanged);
      window.removeEventListener(SUPPORT_CHAT_UNREAD_REFRESH_EVENT, handleUnreadRefresh);
      clearTimers();
      socketRef.current?.close();
    };
  }, []);

  useEffect(() => {
    refreshUnread().catch(() => undefined);
  }, [location.pathname]);

  if (hidden) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label="在线客服未读消息"
      className="fixed z-50 flex h-11 items-center gap-1.5 rounded-full bg-[#F37021] px-3.5 text-white shadow-[0_10px_22px_rgba(112,61,34,0.18)] transition-transform active:scale-95"
      style={{
        right: "max(18px, calc((100vw - 430px) / 2 + 18px))",
        bottom: "calc(env(safe-area-inset-bottom) + 86px)",
      }}
      onClick={() => navigate("/support-chat")}
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/18">
        <Headphones size={18} strokeWidth={2.4} />
      </span>
      <span className="text-[13px] font-semibold leading-none">客服</span>
      <span className="absolute -right-1.5 -top-1.5 min-w-5 rounded-full border-2 border-white bg-[#E94B3C] px-1 text-center text-[11px] font-bold leading-5 text-white shadow-[0_3px_8px_rgba(88,45,31,0.16)]">
        {unread > 99 ? "99+" : unread}
      </span>
    </button>
  );
}
